/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetAggregateAttribute';

const ActionSetAggregateAttributeManager: ActionManagerFn<
  'ActionSetAggregateAttribute'
> = (platform: Platform): ActionManager<'ActionSetAggregateAttribute'> => {
  return {
    ...baseActionManager(platform, 'ActionSetAggregateAttribute'),
    defaultValue,
    props: {
      ...baseActionProps,
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
      aggregateFunction: {
        label: 'Aggregate Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
      },
      intoAttributeCode: {
        label: 'Into Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
    },
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionQuery' || parent.type === 'TriggerBeforeQuery'
      );
    },
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (
        property === 'attributeCode' &&
        typeof value === 'string' &&
        !itemData.intoAttributeCode
      ) {
        return {
          [property]: value,
          intoAttributeCode: value,
        };
      }
      return {
        [property]: value,
      };
    },
    name: 'Set Aggregate Attribute',
    getDisplayText: (item) =>
      item.props.intoAttributeCode || item.props.attributeCode,
    description: `**Overview**<br/>
The Set Aggregate Attribute action is a query modification component designed to transform a standard data retrieval operation into a server-side calculation. Instead of fetching a list of raw records, this action instructs the database to perform mathematical aggregations (like summing values or counting rows) and return only the calculated results.<br/><br/>
This component cannot exist independently; it is strictly an architectural modifier used within ActionQuery or TriggerBeforeQuery. It allows developers to offload heavy processing to the database engine, making it efficient for generating dashboard metrics and summary statistics without loading thousands of records into the browser's memory.

**Key Features**<br/>
* **Server-Side Optimization:** Performs calculations at the database level, returning only the final numbers (e.g., "500") rather than the entire dataset, significantly reducing network traffic and client-side processing.
* **Standard Aggregations:** Supports the core SQL aggregate functions: Avg (Average), Count, Max (Maximum), Min (Minimum), and Sum.
* **Result Mapping:** Allows the calculated value to be mapped back to a specific attribute in the Data Store (intoAttributeCode), effectively aliasing the result (e.g., SELECT SUM(Amount) AS TotalAmount).
* **Smart Configuration:** Automatically attempts to map the destination attribute to match the source attribute when selected, streamlining the setup process.

**Properties**<br/>
* **Attribute (attributeCode) (Required):** A ViewAttributeChooser property.
  * *Function:* The column or field in the database on which the calculation should be performed (e.g., "OrderAmount").
* **Aggregate Function (Required):** A selection property.
  * *Options:* Avg, Count, Max, Min, Sum.
  * *Function:* Defines the mathematical operation to apply.
* **Into Attribute (intoAttributeCode) (Required):** A ViewAttributeChooser property.
  * *Function:* The attribute in the local Data Store where the result will be stored.
  * *Note:* Often this is a transient or temporary field designed specifically to hold summary data.

**Use Cases**<br/>
* **Dashboard KPIs:** calculating "Total Sales YTD" or "Average Customer Rating" to display in a metric card.
* **Data Limits:** Finding the "Max ID" in a table to determine the starting point for a new sequence.
* **Inventory Checks:** Using Count to check how many items are currently in stock for a specific category.`,
  };
};

export default ActionSetAggregateAttributeManager;
